import type { ElegantRoute, CustomRoute } from "@elegant-router/types";
import type { RouteRecordRaw } from "vue-router";
import { generatedRoutes } from "../elegant/routes";
import { layouts, views } from "../elegant/imports";
import { transformElegantRoutesToVueRoutes } from "../elegant/transform";

/**
 * 自定义路由
 * 这些路由不会自动生成，需要手动配置
 */
const customRoutes: CustomRoute[] = [
  {
    name: "root",
    path: "/",
    component: "layout.base$view.home",
    meta: {
      seoKey: "home",
      noIndex: false,
    },
  },

  {
    name: "exception",
    path: "/exception",
    component: "layout.blank$view.403",
    meta: {
      seoKey: "403",
      noIndex: true,
    },
  },
  {
    name: "not-found",
    path: "/:pathMatch(.*)*",
    component: "layout.blank$view.404",
    meta: {
      seoKey: "404",
      noIndex: true,
    },
  },
];

/**
 * SEO 路由后处理函数 - 为所有路由添加默认SEO元数据
 */
function enhanceRoutesWithSEO(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  const defaultMeta = {
    title: "",
    description: "",
    keywords: "",
    ogImage: "",
  };

  // SEO路由映射表 - 将路由路径映射到SEO配置键
  const seoRouteMapping: Record<string, string> = {
    "/": "home",
    "/service": "service",
    "/about": "about",
    "/question": "question",
    "/contact": "contact",
    "/industry-trends": "industry-trends",
    "/supplier": "supplier",
    "/membership-advantage/smart-ordering-platform":
      "membership-advantage_smart-ordering-platform",
    "/membership-advantage/advanced-tech-system":
      "membership-advantage_advanced-tech-system",
    "/membership-advantage/marketing-promotion":
      "membership-advantage_marketing-promotion",
    "/membership-advantage/financial-tax-consulting":
      "membership-advantage_financial-tax-consulting",
    "/membership-advantage/membership-program":
      "membership-advantage_membership-program",
    "/membership-advantage/procurement-cost-optimization":
      "membership-advantage_procurement-cost-optimization",
    // 法律页面
    "/legal": "legal-terms",
    // 错误页面
    "/404": "404",
    "/403": "403",
    "/500": "500",
  };

  return routes.map((route) => {
    // 创建新的route对象以避免修改原始对象
    const newRoute = { ...route };

    // 初始化meta对象（如果不存在）
    newRoute.meta = { ...defaultMeta, ...(route.meta || {}) };

    // 根据路由路径查找对应的SEO配置
    const seoKey = seoRouteMapping[route.path];

    if (seoKey) {
      // 存储seoKey，让组件可以根据当前语言动态获取SEO数据
      newRoute.meta = {
        ...newRoute.meta,
        seoKey, // 存储seoKey用于后续获取对应语言的SEO数据
      };
    }

    // 处理嵌套路由
    if (route.children && route.children.length > 0) {
      newRoute.children = enhanceRoutesWithSEO(route.children);
    }

    return newRoute;
  });
}

/**
 * 合并自定义路由和自动生成的路由
 */
const elegantRoutes: ElegantRoute[] = [
  ...customRoutes,
  ...generatedRoutes.filter((route) => {
    // 过滤掉已经在自定义路由中定义的路由
    // 使用字符串比较来避免类型问题
    const customNames = customRoutes.map((cr) => cr.name as string);
    return !customNames.includes(route.name as string);
  }),
];

/**
 * 将优雅路由转换为Vue路由
 */
const transformedRoutes = transformElegantRoutesToVueRoutes(
  elegantRoutes,
  layouts,
  views
);

// 对转换后的路由应用SEO增强
const enhancedRoutes = enhanceRoutesWithSEO(transformedRoutes);

// 导出增强后的路由
export const routes = enhancedRoutes;
